import { useAthosContext } from '../fetchers/provider'
import { useOption } from './useOption'
import { parseFilterList, splitFilterPair, getRelatedFilterKeys, parseFilterValueTerms, RANGE_INDICATOR } from '../components/FilterUtils'

export const useFilters = () => {
  const { refine } = useAthosContext()

  const activeFilters = useOption('filters', {
    defaultValue: new Set(),
    syncWithURL: true
  })

  const handleToggleFilter = (filter) => {
    const filters = new Set(activeFilters)
    const targets = parseFilterList(filter)

    targets.forEach((target) => {
      if (target.includes(RANGE_INDICATOR)) {
        const field = target.slice(0, target.indexOf(':'))
        let removedSame = false
        for (const active of [...filters]) {
          if (active.startsWith(`${field}:`) && active.includes(RANGE_INDICATOR)) {
            filters.delete(active)
            if (active === target) removedSame = true
          }
        }
        if (!removedSame) filters.add(target)
        return
      }

      const { field, value } = splitFilterPair(target)
      const related = getRelatedFilterKeys(field, value)
      const isActive = related.some((item) => filters.has(item))
      if (isActive) related.forEach((item) => filters.delete(item))
      else filters.add(`${field}:${parseFilterValueTerms(value)[0] || value}`)
    })

    refine('filters', filters)
    refine('page', 1)
  }

  const handleToggleFilterGroup = (groupFilters) => {
    const filters = new Set(activeFilters)
    const targets = (groupFilters || []).flatMap((item) => parseFilterList(item)).filter(Boolean)
    if (!targets.length) return

    const allActive = targets.every((target) => {
      const { field, value } = splitFilterPair(target)
      return getRelatedFilterKeys(field, value).some((item) => filters.has(item))
    })

    targets.forEach((target) => {
      const { field, value } = splitFilterPair(target)
      const related = getRelatedFilterKeys(field, value)
      const canonical = `${field}:${parseFilterValueTerms(value)[0] || value}`
      if (allActive) related.forEach((item) => filters.delete(item))
      else if (!related.some((item) => filters.has(item))) filters.add(canonical)
    })

    refine('filters', filters)
    refine('page', 1)
  }

  const handleRemoveAllByField = (field) => {
    const filters = new Set(activeFilters)
    const filtersToRemove = Array.from(filters).filter((item) => item.startsWith(`${field}:`))
    filtersToRemove.forEach((item) => filters.delete(item))

    refine('filters', filters)
    refine('page', 1)
  }

  const handleRemoveFilter = (filter) => {
    const filters = new Set(activeFilters)
    const { field, value } = splitFilterPair(filter)
    getRelatedFilterKeys(field, value).forEach((item) => filters.delete(item))
    filters.delete(filter)

    refine('filters', filters)
    refine('page', 1)
  }

  const handleClearFilters = () => {
    refine('filters', new Set([]))
    refine('page', 1)
  }

  return {
    handleToggleFilter,
    handleToggleFilterGroup,
    handleRemoveFilter,
    handleClearFilters,
    handleRemoveAllByField
  }
}
